import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import PostCard from "../components/PostCard";
import Comentarios from "../components/Comentarios";
import NewsletterBlock from "../components/NewsletterBlock";
import ProfileAvatar from "../components/ProfileAvatar";
import useReveal from "../hooks/useReveal";

const CATEGORIAS_FIJAS = [
  "Todos",
  "Reflexión",
  "Rutina",
  "Personas",
  "Tiempo",
  "Memoria",
  "Lecturas",
  "Ciudad",
  "Trabajo",
  "Naturaleza",
  "Notas rápidas",
];

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [destacado, setDestacado] = useState(null);
  const [populares, setPopulares] = useState([]);
  const [activeTag, setActiveTag] = useState("Todos");
  const [subsCount, setSubsCount] = useState(320);
  const [author, setAuthor] = useState(null);

  useReveal();

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data.items || []));
    api.get("/posts/destacado").then((r) => setDestacado(r.data)).catch(() => {});
    api.get("/posts/populares").then((r) => setPopulares(r.data));
    api.get("/newsletter/count").then((r) => setSubsCount(r.data.count)).catch(() => {});
    api.get("/config/author").then((r) => setAuthor(r.data)).catch(() => {});
  }, []);

  const filtered =
    activeTag === "Todos"
      ? posts
      : posts.filter((p) => p.categoria === activeTag);

  return (
    <>
      <Nav />

      {/* HERO */}
      <section className="hero container" data-testid="hero">
        <div className="hero-grid">
          <div>
            <div className="hero-eyebrow reveal">Blog personal · nº 12</div>
            <h1 className="hero-title reveal d1">
              Lo que ocurre <em>entre</em> una cosa y otra también merece ser
              escrito.
            </h1>
            <p className="hero-desc reveal d2">
              Escribo para entender. Emociones, vida cotidiana y viajes. Sin
              editar demasiado, sin pretender demasiado.
            </p>
            <div className="hero-ctas reveal d3">
              <Link to="/escritos" className="btn btn-primary" data-testid="hero-cta-leer">
                Leer entradas →
              </Link>
              <Link to="/sobre-mi" className="btn btn-outline" data-testid="hero-cta-sobre-mi">
                Sobre mí
              </Link>
            </div>
            <div className="hero-stats reveal d4">
              <div>
                <div className="stat-num">{posts.length || 5}</div>
                <div className="stat-label">Entradas</div>
              </div>
              <div>
                <div className="stat-num">+{subsCount}</div>
                <div className="stat-label">Lectores</div>
              </div>
              <div>
                <div className="stat-num">
                  {posts.reduce((a, p) => a + (p.vistas || 0), 0).toLocaleString("es-ES") || "0"}
                </div>
                <div className="stat-label">Lecturas</div>
              </div>
            </div>
          </div>
          <div className="hero-img-wrap reveal d2">
            <img
              src="https://images.unsplash.com/photo-1519681393784-d120267933ba?w=900&q=80"
              alt="Ventana con cortina moviéndose con el viento"
            />
            {destacado && (
              <div className="hero-badge">
                <div className="label">Entrada destacada</div>
                <div className="title">{destacado.titulo}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ULTIMAS ENTRADAS */}
      <section className="section" data-testid="section-ultimas">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="section-eyebrow">Últimas entradas</div>
              <h2 className="section-title">
                Escritos <em>recientes</em>
              </h2>
            </div>
            <Link to="/escritos" className="section-link" data-testid="link-ver-todas">
              Ver archivo completo →
            </Link>
          </div>
          <div className="posts-grid">
            {posts.slice(0, 3).map((p, i) => (
              <PostCard key={p.slug} post={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED POST */}
      {destacado && (
        <section className="section" data-testid="section-destacado">
          <div className="container">
            <div className="section-head">
              <div>
                <div className="section-eyebrow">Destacada esta semana</div>
                <h2 className="section-title">
                  La <em>lectura</em> del mes.
                </h2>
              </div>
            </div>
            <Link
              to={`/entrada/${destacado.slug}`}
              className="featured-post reveal"
              data-testid="featured-post"
            >
              <div className="featured-img">
                <img src={destacado.imagen_url} alt={destacado.titulo} />
              </div>
              <div className="featured-body">
                <div className="eyebrow">{destacado.categoria}</div>
                <h3>{destacado.titulo}</h3>
                <p>{destacado.excerpt}</p>
                <div className="featured-stats">
                  <div>
                    <strong>{destacado.tiempo_lectura} min</strong>
                    <small>Lectura</small>
                  </div>
                  <div>
                    <strong>
                      {(destacado.vistas || 0).toLocaleString("es-ES")}
                    </strong>
                    <small>Vistas</small>
                  </div>
                  <div>
                    <strong>{formatDate(destacado.fecha_publicacion)}</strong>
                    <small>Publicado</small>
                  </div>
                </div>
                <span className="btn btn-primary">Leer entrada →</span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* POPULAR + SIDEBAR */}
      <section id="populares" className="section" data-testid="section-populares">
        <div className="container">
          <div className="popular-layout">
            <div>
              <div className="section-head">
                <div>
                  <div className="section-eyebrow">Lo más leído</div>
                  <h2 className="section-title">
                    Cuatro entradas que <em>siguen vivas</em>.
                  </h2>
                </div>
              </div>
              <div className="popular-list">
                {populares.map((p, i) => (
                  <Link
                    key={p.slug}
                    to={`/entrada/${p.slug}`}
                    className="popular-item reveal"
                    data-testid={`popular-item-${p.slug}`}
                  >
                    <div className="popular-num">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="popular-body">
                      <div className="popular-tag">{p.categoria}</div>
                      <div className="popular-title">{p.titulo}</div>
                      <div className="popular-meta">
                        {formatDate(p.fecha_publicacion)} · {p.tiempo_lectura}{" "}
                        min
                      </div>
                    </div>
                    <div className="popular-views">
                      {(p.vistas || 0).toLocaleString("es-ES")} lecturas
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <aside>
              <div className="sidebar-card reveal">
                <ProfileAvatar
                  className="sidebar-avatar"
                  src={author?.foto_url}
                  name={author?.nombre || "Berenice"}
                  size={64}
                  testId="sidebar-avatar"
                />
                <h4>{author?.nombre || "Berenice"}</h4>
                <p>
                  {author?.bio?.slice(0, 180) ||
                    "Escribo este cuaderno desde un piso pequeño en Madrid. Leo poco y releo mucho. Creo en los párrafos que no necesitan ser subrayados."}
                </p>
                <Link to="/newsletter" className="btn btn-accent btn-sm" data-testid="sidebar-cta-newsletter">
                  Recibir la carta mensual →
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* TEMAS */}
      <section id="temas" className="section" data-testid="section-temas">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="section-eyebrow">Temas</div>
              <h2 className="section-title">
                Explora por <em>asunto</em>.
              </h2>
            </div>
          </div>
          <div className="tags-cloud reveal">
            {CATEGORIAS_FIJAS.map((t) => (
              <button
                key={t}
                className={`tag-btn ${activeTag === t ? "active" : ""}`}
                onClick={() => setActiveTag(t)}
                data-testid={`tag-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 40 }}>
            <div className="posts-grid">
              {filtered.slice(0, 3).map((p, i) => (
                <PostCard key={p.slug} post={p} index={i} />
              ))}
            </div>
            {filtered.length === 0 && (
              <p style={{ color: "var(--ink-3)", textAlign: "center", padding: 40 }}>
                No hay entradas publicadas en esta categoría todavía.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* COMENTARIOS de destacado */}
      {destacado && (
        <section className="section" data-testid="section-comentarios">
          <div className="container">
            <div className="section-head" style={{ justifyContent: "center", textAlign: "center" }}>
              <div>
                <div className="section-eyebrow">Conversación</div>
                <h2 className="section-title" style={{ margin: "0 auto" }}>
                  Lecturas de <em>"{destacado.titulo}"</em>.
                </h2>
              </div>
            </div>
            <Comentarios slug={destacado.slug} />
          </div>
        </section>
      )}

      {/* SOBRE MI strip */}
      <section className="section" data-testid="section-sobre-mi">
        <div className="container">
          <div className="about-strip reveal">
            <ProfileAvatar
              src={author?.foto_url}
              name={author?.nombre || "Berenice"}
              size={180}
              testId="about-strip-avatar"
            />
            <div>
              <div className="section-eyebrow">Sobre mí</div>
              <h3>{author?.nombre || "Berenice"}</h3>
              <p>
                {author?.bio ||
                  "Escribo para entender mis emociones y las de los demás. Esto es Berenice — Sin Filtro: un ejercicio personal en público. Sin filtros, sin categoría fija."}
              </p>
            </div>
            <div className="social-row">
              <Link to="/sobre-mi" className="btn btn-outline btn-sm" data-testid="about-strip-cta">
                Leer mi historia →
              </Link>
              <a
                href="mailto:hola@berenice-sinfiltro.blog"
                className="btn btn-outline btn-sm"
                data-testid="about-strip-email"
              >
                Escribirme
              </a>
            </div>
          </div>
        </div>
      </section>

      <NewsletterBlock />
      <Footer />
    </>
  );
}
